// Public status page — thin adapter over valoi_engine's UNAUTHENTICATED
// /api/v1/status/summary (see app/routers/status_api.py, app/db/repos.py::SliRepo.
// daily_service_history). Deliberately a much smaller surface than adminService.ts/
// sliService.ts: no provider names, no cost figures, no error detail — just a
// service name, a per-day status level, and an uptime percentage.

import { getJson } from '@/shared/api/apiClient';

export type StatusLevel = 'operational' | 'degraded' | 'partial_outage' | 'major_outage';

export type StatusDay = {
  date: string;
  status: StatusLevel;
  uptimePct: number | null;
  // One status per UTC hour (24 entries) — how the hover popover shows real
  // "N hrs degraded" figures without a second request. See daily_service_history's
  // docstring: this is a rollup of the same data as `status`, not an incident log.
  hourly: StatusLevel[];
};

export type ServiceStatus = {
  name: string;
  status: StatusLevel;
  uptimePct: number | null;
  daily: StatusDay[];
};

export type StatusSummary = {
  generatedAt: string;
  days: number;
  overallStatus: StatusLevel;
  services: ServiceStatus[];
};

type StatusDayApiItem = { date: string; status: string; uptime_pct: number | null; hourly: string[] };
type ServiceStatusApiItem = {
  name: string;
  status: string;
  uptime_pct: number | null;
  daily: StatusDayApiItem[];
};
type StatusSummaryApiResponse = {
  generated_at: string;
  days: number;
  overall_status: string;
  services: ServiceStatusApiItem[];
};

// The backend's status is an open `str` (room for a level this build predates);
// an unrecognized value fails to 'major_outage' rather than crashing the banner
// on a string it doesn't know how to color. Fails LOUD, not quiet: this is a
// status page, so a value this build can't interpret must read as "something's
// wrong, go look" rather than silently rendering as healthy and hiding a real
// outage behind a green checkmark.
const STATUS_LEVELS: readonly string[] = ['operational', 'degraded', 'partial_outage', 'major_outage'];

function toStatusLevel(raw: string): StatusLevel {
  return (STATUS_LEVELS.includes(raw) ? raw : 'major_outage') as StatusLevel;
}

// GET /api/v1/status/summary?days=90 — per-service daily status/uptime for the
// trailing `days` days, plus each service's current status and an overall rollup.
export async function getStatusSummary(days = 90): Promise<StatusSummary> {
  const body = await getJson<StatusSummaryApiResponse>(`/api/v1/status/summary?days=${days}`);
  return {
    generatedAt: body.generated_at,
    days: body.days,
    overallStatus: toStatusLevel(body.overall_status),
    services: body.services.map((svc) => ({
      name: svc.name,
      status: toStatusLevel(svc.status),
      uptimePct: svc.uptime_pct,
      daily: svc.daily.map((d) => ({
        date: d.date,
        status: toStatusLevel(d.status),
        uptimePct: d.uptime_pct,
        hourly: d.hourly.map(toStatusLevel),
      })),
    })),
  };
}
